import {useCallback, useMemo, useState} from 'react'
import {type StyleProp, StyleSheet, View, type ViewStyle} from 'react-native'
import {
  type AppBskyFeedDefs,
  AppBskyFeedPost,
  AtUri,
  moderatePost,
  type ModerationDecision,
  RichText as RichTextAPI,
} from '@atproto/api'
import {useQueryClient} from '@tanstack/react-query'

import {MAX_POST_LINES} from '#/lib/constants'
import {materialRefFromRecord} from '#/lib/document/material'
import {documentAudienceFromPost, postEmbedView} from '#/lib/document/types'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {makeProfileLink} from '#/lib/routes/links'
import {countLines} from '#/lib/strings/helpers'
import {
  POST_TOMBSTONE,
  type Shadow,
  usePostShadow,
} from '#/state/cache/post-shadow'
import {isClassroomPostVisibleTo} from '#/state/classrooms'
import {useClassroomPostRole} from '#/state/classrooms/account'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {unstableCacheProfileView} from '#/state/queries/profile'
import {Link} from '#/view/com/util/Link'
import {PostMeta} from '#/view/com/util/PostMeta'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, select, useTheme} from '#/alf'
import {
  CapsuleFeedEmbed,
  hasCapsuleEmbed,
} from '#/components/capsules/CapsuleFeedEmbed'
import {
  ClassroomPostByline,
  ClassroomPostLocked,
  useLiveClassroomPostMark,
} from '#/components/classrooms/ClassroomPostByline'
import {
  FlashBookFeedEmbed,
  hasFlashbookEmbed,
} from '#/components/flashbooks/FlashBookFeedEmbed'
import {
  GalleryBleed,
  maybeApplyGalleryOffsetStyles,
} from '#/components/images/Gallery'
import {ContentHider} from '#/components/moderation/ContentHider'
import {LabelsOnMyPost} from '#/components/moderation/LabelsOnMe'
import {PostAlerts} from '#/components/moderation/PostAlerts'
import {
  hasModuleEmbed,
  ModuleFeedEmbed,
} from '#/components/modules/ModuleFeedEmbed'
import {Embed, PostEmbedViewContext} from '#/components/Post/Embed'
import {PostBody} from '#/components/Post/PostBody'
import {PostRepliedTo} from '#/components/Post/PostRepliedTo'
import {TranslatedPost} from '#/components/Post/Translated'
import {PostControls} from '#/components/PostControls'
import {type ClassroomPostAction} from '#/components/PostControls/PostMenu/PostMenuItems'
import {SubtleHover} from '#/components/SubtleHover'
import * as bsky from '#/types/bsky'

export function Post({
  post,
  showReplyLine,
  hideTopBorder,
  style,
  onBeforePress,
  classroomAction,
  inClassroom,
}: {
  post: AppBskyFeedDefs.PostView
  showReplyLine?: boolean
  hideTopBorder?: boolean
  style?: StyleProp<ViewStyle>
  onBeforePress?: () => void
  /** Set when this post is being read inside a classroom. */
  classroomAction?: ClassroomPostAction
  /**
   * Set when this post is drawn in the room it was written in (the room's own
   * Posts list), where the room's name is already on the header and the
   * classroom byline would only repeat it.
   */
  inClassroom?: boolean
}) {
  const moderationOpts = useModerationOpts()
  const record = useMemo<AppBskyFeedPost.Record | undefined>(
    () =>
      bsky.validate(post.record, AppBskyFeedPost.validateRecord)
        ? post.record
        : undefined,
    [post],
  )
  const postShadowed = usePostShadow(post)
  const richText = useMemo(
    () =>
      record
        ? new RichTextAPI({
            text: record.text,
            facets: record.facets,
          })
        : undefined,
    [record],
  )
  const moderation = useMemo(
    () => (moderationOpts ? moderatePost(post, moderationOpts) : undefined),
    [moderationOpts, post],
  )
  if (postShadowed === POST_TOMBSTONE) {
    return null
  }
  if (record && richText && moderation) {
    return (
      <PostInner
        post={postShadowed}
        record={record}
        richText={richText}
        moderation={moderation}
        showReplyLine={showReplyLine}
        hideTopBorder={hideTopBorder}
        style={style}
        onBeforePress={onBeforePress}
        classroomAction={classroomAction}
        inClassroom={inClassroom}
      />
    )
  }
  return null
}

function PostInner({
  post,
  record,
  richText,
  moderation,
  showReplyLine,
  hideTopBorder,
  style,
  onBeforePress: outerOnBeforePress,
  classroomAction,
  inClassroom,
}: {
  post: Shadow<AppBskyFeedDefs.PostView>
  record: AppBskyFeedPost.Record
  richText: RichTextAPI
  moderation: ModerationDecision
  showReplyLine?: boolean
  hideTopBorder?: boolean
  style?: StyleProp<ViewStyle>
  onBeforePress?: () => void
  classroomAction?: ClassroomPostAction
  inClassroom?: boolean
}) {
  const queryClient = useQueryClient()
  const t = useTheme()
  const {openComposer} = useOpenComposer()
  /*
   * The embed this post draws: the view the app view served, or one built from the
   * post's own record when it served none (see `postEmbedView`).
   */
  const embedView = useMemo(
    () => postEmbedView({embed: post.embed, record}),
    [post.embed, record],
  )
  const [limitLines, setLimitLines] = useState(
    () => countLines(richText?.text) >= MAX_POST_LINES,
  )
  const itemUrip = new AtUri(post.uri)
  const itemHref = makeProfileLink(post.author, 'post', itemUrip.rkey)
  let replyAuthorDid = ''
  if (record.reply) {
    const urip = new AtUri(record.reply.parent?.uri || record.reply.root.uri)
    replyAuthorDid = urip.hostname
  }

  const onPressReply = useCallback(() => {
    openComposer({
      replyTo: {
        uri: post.uri,
        cid: post.cid,
        text: record.text,
        author: post.author,
        embed: post.embed,
        moderation,
        langs: record.langs,
      },
      logContext: 'PostReply',
    })
  }, [openComposer, post, record, moderation])

  const onPressShowMore = useCallback(() => {
    setLimitLines(false)
  }, [setLimitLines])

  const onBeforePress = useCallback(() => {
    unstableCacheProfileView(queryClient, post.author)
    outerOnBeforePress?.()
  }, [queryClient, post.author, outerOnBeforePress])

  const [hover, setHover] = useState(false)

  /*
   * A room's post, read by somebody it was not written for (the room is private,
   * or the post is addressed to less than everybody). It says which room it
   * belongs to and who it was for, and shows nothing of the post itself.
   */
  const classroomMark = useLiveClassroomPostMark(post)
  const classroomRole = useClassroomPostRole(classroomMark)
  const classroomLocked =
    !!classroomMark &&
    !isClassroomPostVisibleTo(classroomMark, {role: classroomRole})
  const showClassroomByline = !!classroomMark && !inClassroom

  if (classroomLocked && classroomMark) {
    return (
      <View
        testID="post-classroomLocked"
        style={[
          styles.outer,
          t.atoms.border_contrast_low,
          !hideTopBorder && a.border_t,
          style,
        ]}>
        <View style={[a.pt_sm]}>
          <ClassroomPostLocked
            mark={classroomMark}
            author={post.author}
            moderation={moderation}
            timestamp={post.indexedAt}
            postHref={itemHref}
          />
        </View>
      </View>
    )
  }

  return (
    <GalleryBleed>
      <Link
        href={itemHref}
        style={[
          styles.outer,
          t.atoms.border_contrast_low,
          !hideTopBorder && a.border_t,
          style,
        ]}
        onBeforePress={onBeforePress}
        onPointerEnter={() => {
          setHover(true)
        }}
        onPointerLeave={() => {
          setHover(false)
        }}>
        <SubtleHover hover={hover} />
        {showReplyLine && (
          <View
            style={[
              styles.replyLine,
              {
                backgroundColor: select(t.name, {
                  light: t.palette.contrast_100,
                  dim: t.palette.contrast_200,
                  dark: t.palette.contrast_200,
                }),
              },
            ]}
          />
        )}
        {showClassroomByline && classroomMark ? (
          <View style={styles.classroomHeader}>
            <ClassroomPostByline
              mark={classroomMark}
              author={post.author}
              moderation={moderation}
              timestamp={post.indexedAt}
              postHref={itemHref}
            />
          </View>
        ) : null}
        <View style={styles.layout}>
          {showClassroomByline ? null : (
            <View style={styles.layoutAvi}>
              <PreviewableUserAvatar
                size={42}
                profile={post.author}
                moderation={moderation.ui('avatar')}
                type={post.author.associated?.labeler ? 'labeler' : 'user'}
              />
            </View>
          )}
          <View
            style={[
              styles.layoutContent,
              maybeApplyGalleryOffsetStyles('meta', {
                post,
                modui: moderation.ui('contentList'),
                additionalCauses: [],
              }),
            ]}>
            {showClassroomByline ? null : (
              <PostMeta
                author={post.author}
                moderation={moderation}
                timestamp={post.indexedAt}
                postHref={itemHref}
              />
            )}
            {replyAuthorDid !== '' && (
              <PostRepliedTo parentAuthor={replyAuthorDid} />
            )}
            <LabelsOnMyPost post={post} />
            <ContentHider
              modui={moderation.ui('contentView')}
              style={styles.contentHider}
              childContainerStyle={styles.contentHiderChild}>
              <PostAlerts
                modui={moderation.ui('contentView')}
                style={[a.pb_xs]}
              />
              {richText.text ? (
                <View style={[a.mb_2xs]}>
                  <PostBody
                    post={post}
                    value={richText}
                    testID="postText"
                    enableTags
                    numberOfLines={limitLines ? MAX_POST_LINES : undefined}
                    textStyle={[a.flex_1, a.text_md]}
                    authorHandle={post.author.handle}
                    shouldProxyLinks={true}
                    onPressShowMore={onPressShowMore}
                  />
                </View>
              ) : undefined}
              <TranslatedPost hideTranslateLink post={post} />
              {hasModuleEmbed(post) ? (
                /*
                 * Modules, books and capsules are drawn as their own cards
                 * wherever a post is drawn (e.g. a classroom's list), rather
                 * than falling through to the embed and reading as a video clip.
                 */
                <View style={[a.pb_xs]}>
                  <ModuleFeedEmbed post={post} />
                </View>
              ) : hasFlashbookEmbed(post) ? (
                <View style={[a.pb_xs]}>
                  <FlashBookFeedEmbed post={post} />
                </View>
              ) : hasCapsuleEmbed(post) ? (
                <View style={[a.pb_xs]}>
                  <CapsuleFeedEmbed post={post} />
                </View>
              ) : embedView ? (
                <View
                  style={maybeApplyGalleryOffsetStyles('embed', {
                    post,
                    modui: moderation.ui('contentList'),
                    additionalCauses: [],
                  })}>
                  <Embed
                    embed={embedView}
                    moderation={moderation}
                    viewContext={PostEmbedViewContext.Feed}
                    authorDid={post.author.did}
                    documentAudience={documentAudienceFromPost(post.record)}
                    documentMaterial={materialRefFromRecord(post.record)}
                    postUri={post.uri}
                  />
                </View>
              ) : null}
            </ContentHider>
            <PostControls
              post={post}
              record={record}
              richText={richText}
              onPressReply={onPressReply}
              logContext="Post"
              classroomAction={classroomAction}
            />
          </View>
        </View>
      </Link>
    </GalleryBleed>
  )
}

const styles = StyleSheet.create({
  outer: {
    paddingTop: 10,
    paddingRight: 15,
    paddingBottom: 5,
    paddingLeft: 10,
    // @ts-ignore web only -prf
    cursor: 'pointer',
  },
  layout: {
    flexDirection: 'row',
    gap: 10,
  },
  layoutAvi: {
    paddingLeft: 8,
  },
  layoutContent: {
    flex: 1,
  },
  classroomHeader: {
    paddingLeft: 8,
    paddingBottom: 12,
  },
  alert: {
    marginBottom: 6,
  },
  replyLine: {
    position: 'absolute',
    left: 36,
    top: 70,
    bottom: 0,
    borderLeftWidth: 2,
  },
  contentHider: {
    marginBottom: 2,
  },
  contentHiderChild: {
    marginTop: 6,
  },
})
